import { useEffect, useState } from "react"
import { authClient } from "../utils/amplifyClient";
import { v4 as uuidv4 } from 'uuid';
import { listDocuments as listDocumentsQuery, listVideos as listVideosQuery } from "../graphql/queries";
import { parseAssignedLessons } from "./pages-daily-briefing/dailyBriefingData";
import * as Sentry from "@sentry/browser";
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    Container,
    Header,
    Calendar,
    Box,
} from './legacy-widgets';
import { useUserContext } from "../contexts/UserContext";


type FileItem = {
    id: string;
    name: string;
    type: string;
    date: string;
    folderName: string | null | undefined;
};

type CalendarEvent = {
    date: Date;
    files: FileItem[];
};

const updateDocumentCustomMutation = /* GraphQL */ `
  mutation UpdateDocument($input: UpdateDocumentInput!) {
    updateDocument(input: $input) {
      id
      title
      assignedSafetyLesson {
        type
        companyId
        date
      }
    }
  }
`;

const updateVideoCustomMutation = /* GraphQL */ `
  mutation UpdateVideo($input: UpdateVideoInput!) {
    updateVideo(input: $input) {
      id
      title
      assignedSafetyLesson {
        type
        companyId
        date
      }
    }
  }
`;

export const ScheduleSafety = () => {

    const [, setLoading] = useState(false)
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [filesList, setFilesList] = useState<FileItem[]>([])
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [allFiles, setAllFiles] = useState<FileItem[]>([]);

    const { company, companyOwner } = useUserContext()

    const companyId = (company?.id as string) || companyOwner?.companyId;
    // TODO: depending on Mark S - potentially set this up so a "companyUser" with "manage-user" permissions can also update this, need to check for companyUser and use the company.id instead of the storedCompanyId, should be a small change.  Need to set up a route with permissions for "manage-users" to see the safety content

    useEffect(() => {
        const loadData = async () => {
            try {
                // Set loading state once at the beginning
                setLoading(true);

                // Run both data fetching operations in parallel
                const [pdfData, videoData] = await Promise.all([
                    fetchPDFs(),
                    fetchVideos()
                ]);

                // Process the results
                const allFilesData = [...pdfData, ...videoData];

                const isFileOnCalendar = (fileId: string) => {
                    return events.some(event =>
                        event.files.some(file => file.id === fileId)
                    );
                };

                // Filter files without dates and not already on calendar
                const availableFiles = allFilesData.filter(file =>
                    !file.date && !isFileOnCalendar(file.id)
                );
                setFilesList(availableFiles);
                setAllFiles(availableFiles);

                // Initialize calendar events with files that have dates
                initializeCalendarEvents(allFilesData);
            } catch (error) {
                console.error('Error loading data:', error);
                Sentry.captureException(error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const fetchPDFs = async (): Promise<FileItem[]> => {
        if (!companyId) return [];

        try {
            const response = await authClient.graphql({
                query: listDocumentsQuery,
            });

            const filteredDocuments = response.data.listDocuments.items.filter(
                doc => (doc.sharedWithCompanyIds?.includes(companyId) && doc.safetyLesson === true)
            );
            return filteredDocuments.map(doc => {
                const assignedLesson = parseAssignedLessons(doc.assignedSafetyLesson).find(
                    lesson => lesson && lesson.companyId === companyId
                );

                return {
                    id: doc.id,
                    name: doc.title,
                    type: 'document',
                    date: assignedLesson?.date || '',
                    folderName: doc.folderName
                };
            });
        } catch (error) {
            console.error('Error fetching PDF files:', error);
            Sentry.captureException(error);
            return [];
        }
    };

    const fetchVideos = async (): Promise<FileItem[]> => {
        if (!companyId) return [];

        try {
            const response = await authClient.graphql({
                query: listVideosQuery,
            });

            // Check if video is shared with company and is marked as a safety lesson
            const filteredVideos = response.data.listVideos.items.filter(
                vid => vid.sharedWithCompanyIds?.includes(companyId) && vid.safetyLesson === true
            );

            return filteredVideos.map(video => {
                const assignedLesson = parseAssignedLessons(video.assignedSafetyLesson).find(
                    lesson => lesson && lesson.companyId === companyId
                );

                return {
                    id: video.id,
                    name: video.title,
                    type: 'video',
                    date: assignedLesson?.date || '',
                    folderName: video.folderName
                };
            });
        } catch (error) {
            console.error('Error fetching video files:', error);
            Sentry.captureException(error);
            return [];
        }
    };

    const initializeCalendarEvents = (files: FileItem[]) => {
        // Collect all files that have dates assigned
        const filesWithDates = files.filter(file => file.date);

        // Group files by date
        const eventsByDate = new Map<string, FileItem[]>();

        filesWithDates.forEach(file => {
            // Convert UTC ISO string to local date
            const utcDate = new Date(file.date);
            // Create a date object that represents the same calendar date in local timezone
            const localDate = new Date(
                utcDate.getFullYear(),
                utcDate.getMonth(),
                utcDate.getDate()
            );
            const dateString = localDate.toDateString();

            if (!eventsByDate.has(dateString)) {
                eventsByDate.set(dateString, []);
            }
            eventsByDate.get(dateString)?.push(file);
        });

        // Create calendar events from grouped files
        const initialEvents: CalendarEvent[] = [];
        eventsByDate.forEach((files, dateString) => {
            initialEvents.push({
                date: new Date(dateString),
                files
            });
        });
        setEvents(initialEvents);
    };

    const addFileToDate = async (file: FileItem, date: Date) => {
        const dateString = date.toDateString();
        const existingEventIndex = events.findIndex(
            event => event.date.toDateString() === dateString
        );

        setFilesList(prevFiles => prevFiles.filter(f => f.id !== file.id));

        try {
            let currentItem;
            if (file.type === 'document') {
                const response = await authClient.graphql({
                    query: listDocumentsQuery,
                    variables: { filter: { id: { eq: file.id } } }
                });
                currentItem = response.data.listDocuments.items[0];
            } else {
                const response = await authClient.graphql({
                    query: listVideosQuery,
                    variables: { filter: { id: { eq: file.id } } }
                });
                currentItem = response.data.listVideos.items[0];
            }

            const localDate = new Date(date);
            localDate.setHours(0, 0, 0, 0);

            const isoDate = new Date(
                localDate.getFullYear(),
                localDate.getMonth(),
                localDate.getDate(),
                0, 0, 0
            ).toISOString();

            const newAssignment = {
                id: uuidv4(),
                type: file.type,
                companyId: companyId,
                date: isoDate
            }

            // Combine existing assignments with the new one
            const existingAssignments = parseAssignedLessons(currentItem.assignedSafetyLesson)
                .filter(item => item !== null)
                .map(item => ({
                    id: item?.id,
                    type: item?.type,
                    companyId: item?.companyId,
                    date: item?.date
                }));

            const updatedAssignments = [...existingAssignments, newAssignment];
            if (file.type === 'document') {
                await authClient.graphql({
                    query: updateDocumentCustomMutation,
                    variables: {
                        input: {
                            id: file.id,
                            assignedSafetyLesson: updatedAssignments
                        }
                    }
                });
            } else if (file.type === 'video') {
                await authClient.graphql({
                    query: updateVideoCustomMutation,
                    variables: {
                        input: {
                            id: file.id,
                            assignedSafetyLesson: updatedAssignments
                        }
                    }
                });
            }

            if (existingEventIndex >= 0) {
                // Add to existing event
                const updatedEvents = [...events];
                updatedEvents[existingEventIndex] = {
                    ...updatedEvents[existingEventIndex],
                    files: [...updatedEvents[existingEventIndex].files, file]
                };
                setEvents(updatedEvents);
            } else {
                // Create new event
                setEvents([...events, { date, files: [file] }]);
            }

        } catch (error) {
            console.error('Error updating safety lesson assignment:', error);
            Sentry.captureException(error);

            // If there's an error, add the file back to the available list
            setFilesList(prevFiles => prevFiles.filter(f => f.id !== file.id));
            setAllFiles(prevAllFiles => prevAllFiles.filter(f => f.id !== file.id));
        }
    };

    const removeFileFromDate = async (fileId: string, date: Date) => {
        const dateString = date.toDateString();
        const existingEventIndex = events.findIndex(
            event => event.date.toDateString() === dateString
        );

        if (existingEventIndex >= 0) {
            // Find the file before removing it from the event
            const fileToRestore = events[existingEventIndex].files.find(file => file.id === fileId);

            if (fileToRestore) {
                try {
                    // Get the current document or video to access its assignedSafetyLesson array
                    let currentItem;
                    if (fileToRestore.type === 'document') {
                        const response = await authClient.graphql({
                            query: listDocumentsQuery,
                            variables: { filter: { id: { eq: fileId } } }
                        });
                        currentItem = response.data.listDocuments.items[0];
                    } else {
                        const response = await authClient.graphql({
                            query: listVideosQuery,
                            variables: { filter: { id: { eq: fileId } } }
                        });
                        currentItem = response.data.listVideos.items[0];
                    }
                    const localDate = new Date(date);
                    localDate.setHours(0, 0, 0, 0);

                    const localDateString = localDate.toISOString().split('T')[0];

                    const existingAssignments = parseAssignedLessons(currentItem.assignedSafetyLesson).map(item => ({
                        id: item?.id,
                        type: item?.type,
                        companyId: item?.companyId,
                        date: item?.date
                    }));
                    // Filter out the assignment for this date
                    const updatedAssignments = existingAssignments.filter(assignment => {
                        const assignmentDatePart = assignment.date?.split('T')[0];
                        return assignmentDatePart !== localDateString || assignment.companyId !== companyId;
                    });

                    // Update the document or video
                    if (fileToRestore.type === 'document') {
                        await authClient.graphql({
                            query: updateDocumentCustomMutation,
                            variables: {
                                input: {
                                    id: fileId,
                                    assignedSafetyLesson: updatedAssignments
                                }
                            }
                        });
                    } else {
                        await authClient.graphql({
                            query: updateVideoCustomMutation,
                            variables: {
                                input: {
                                    id: fileId,
                                    assignedSafetyLesson: updatedAssignments
                                }
                            }
                        });

                    }

                    // Update local state
                    const updatedEvents = [...events];
                    const updatedFiles = updatedEvents[existingEventIndex].files.filter(
                        file => file.id !== fileId
                    );

                    if (updatedFiles.length === 0) {
                        // Remove the entire event if no files remain
                        updatedEvents.splice(existingEventIndex, 1);
                    } else {
                        // Update the event with remaining files
                        updatedEvents[existingEventIndex] = {
                            ...updatedEvents[existingEventIndex],
                            files: updatedFiles
                        };
                    }

                    setEvents(updatedEvents);

                    // Add the file back to the files list directly
                    setFilesList(prevFiles => [fileToRestore, ...prevFiles]);
                    setAllFiles(prevAllFiles => [fileToRestore, ...prevAllFiles]);

                } catch (error) {
                    console.error('Error removing safety lesson assignment:', error);
                    Sentry.captureException(error);
                }
            }
        }
    };

    const DraggableFile = ({ file }: { file: FileItem }) => {
        const [{ isDragging }, drag] = useDrag(() => ({
            type: 'file',
            item: file,
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging()
            })
        }));

        return (
            <div
                ref={drag}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    cursor: 'move',
                    padding: '8px',
                    margin: '4px 0',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9',
                    fontSize: '10px'
                }}
            >
                {file.folderName} -- {file.name} -- {file.type}
            </div>
        );
    };

    const DroppableDay = ({ date }: { date: Date }) => {
        const [{ isOver }, drop] = useDrop(() => ({
            accept: 'file',
            drop: (item: FileItem) => addFileToDate(item, date),
            collect: (monitor) => ({
                isOver: !!monitor.isOver()
            })
        }));

        // Find files for this date
        const dateString = date.toDateString();
        const event = events.find(e => e.date.toDateString() === dateString);
        const filesForDate = event ? event.files : [];

        return (
            <div
                ref={drop}
                style={{
                    height: '100%',
                    minHeight: '50px',
                    maxHeight: '60px',
                    padding: '2px',
                    backgroundColor: isOver ? '#e6f7ff' : 'transparent',
                    border: isOver ? '1px dashed #1890ff' : 'none',
                    overflowY: 'auto', // Add vertical scroll
                }}
            >
                {filesForDate.length > 0 && (
                    <div style={{ fontSize: '12px' }}>
                        {filesForDate.map(file => (
                            <div key={file.id} style={{
                                margin: '2px 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '8px'
                            }}>
                                <span>{file.folderName ? `${file.folderName}: ` : ''}{file.name}</span>                                <button
                                    onClick={() => removeFileFromDate(file.id, date)}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        color: 'red',
                                        padding: '0 4px',
                                        fontSize: '18px'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        console.log("month changed")

        // if I need to fetch events for this month do it here TODO:
    }, [selectedDate])






    return (
        <div className="page-container schedule-safety">
            <h3>Schedule Safety</h3>

            <DndProvider backend={HTML5Backend}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '20px',
                    height: 'calc(100vh - 150px)'
                }}>
                    {/* Left column with files */}
                    <div style={{
                        width: '300px',
                        flexShrink: 0,
                        borderRight: '1px solid #ccc',
                        padding: '10px'
                    }}>
                        <Container>
                            <div style={{ width: '100%', margin: '0 auto', transform: 'scale(0.8)', transformOrigin: 'top', display: 'flex', marginBottom: '-40px', marginTop: "-10px" }}>
                                <Calendar
                                    granularity="month"
                                    value={selectedDate}
                                    onChange={({ detail }) => {
                                        setSelectedDate(detail.value)
                                    }}
                                />
                            </div>
                            <Header variant="h3">Available Files to Schedule</Header>
                            <div>
                                <Box margin={{ bottom: 'xs' }}>
                                    <input
                                        type="text"
                                        placeholder="Search files..."
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px'
                                        }}
                                        value={searchTerm}
                                        onChange={(e) => {
                                            const newSearchTerm = e.target.value.toLowerCase();
                                            setSearchTerm(newSearchTerm);

                                            if (newSearchTerm === '') {
                                                // If search is cleared, restore all files
                                                setFilesList(allFiles);
                                            } else {
                                                // Filter files based on search term
                                                const filteredFiles = allFiles.filter(file =>
                                                (file.name?.toLowerCase().includes(newSearchTerm) ||
                                                    file.folderName?.toLowerCase().includes(newSearchTerm))
                                                );
                                                setFilesList(filteredFiles);
                                            }
                                        }}
                                    />
                                </Box>
                                <div
                                    style={{
                                        maxHeight: '325px',
                                        overflowY: 'scroll',
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: "#666 #ddd",
                                        marginTop: '5px',

                                    }}>
                                    {filesList
                                        .sort((a, b) => {
                                            const folderA = a.folderName || '';
                                            const folderB = b.folderName || '';
                                            return folderA.localeCompare(folderB);
                                        })
                                        .map(file => (
                                            <DraggableFile key={file.id} file={file} />
                                        ))}
                                </div>
                            </div>                        </Container>
                    </div>


                    {/* Right side with calendar */}
                    <div style={{ flex: 1, overflow: "auto" }}>
                        <Container>
                            <div style={{
                                marginTop: '20px',
                                maxWidth: '900px',
                                margin: '0 auto'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '4px',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                }}>
                                    <div>Sun</div>
                                    <div>Mon</div>
                                    <div>Tue</div>
                                    <div>Wed</div>
                                    <div>Thu</div>
                                    <div>Fri</div>
                                    <div>Sat</div>
                                </div>
                                <div style={{
                                    marginTop: '10px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '8px',
                                    fontSize: '12px',
                                }}>


                                    {(() => {
                                        // Fix the month calculation - months are 0-indexed in JavaScript
                                        const currentMonth = parseInt(selectedDate.substring(5, 7)) - 1;
                                        const currentYear = parseInt(selectedDate.substring(0, 4));

                                        // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
                                        const firstDay = new Date(currentYear, currentMonth, 1).getDay();

                                        // Get the number of days in the month
                                        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

                                        // Create the calendar grid
                                        return Array.from({ length: 42 }, (_, i) => {
                                            const dayIndex = i - firstDay;
                                            const day = dayIndex + 1;

                                            if (dayIndex < 0 || day > daysInMonth) {
                                                // Empty cell for days outside current month
                                                return <div key={i} style={{ padding: '4px' }}></div>;
                                            }

                                            // Create a date object for this day
                                            const currentDate = new Date(currentYear, currentMonth, day);

                                            return (
                                                <div key={i} style={{ border: '1px solid #ccc', padding: '4px' }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{day}</div>
                                                    <DroppableDay date={currentDate} />
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </Container>
                    </div>
                </div >
            </DndProvider >
        </div >

    )
}